/**
 * Settings > Claude: "Starter kit" (PR #67, docs/mcp/starter-kit).
 *
 * Builds the user's dealcloser-messaging skill from a campaign brief plus
 * their Offer, Voice, FAQ and Objection settings, so Claude writes in their
 * words from the first run. Copy it, or download the zip claude.ai's skill
 * upload takes. Building it reads only; nothing is saved.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Check, Copy, Download, Loader2, MessageSquareText, Sparkles } from 'lucide-react'
import {
  normalizeCampaignBrief,
  type FaqConfig,
  type ObjectionConfig,
  type OfferConfig,
  type SenderVoiceConfig,
} from '@dealcloser/shared'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { useApi } from '@/hooks/use-api'
import { useCampaignBrief, useCampaigns } from '@/features/campaigns/use-campaigns'
import { readMessageWriter } from './mcp-helpers'
import { ASK_CLAUDE_EXAMPLES, SKILL_NAME, buildMessagingSkill, skillZip } from './starter-kit'

function useSetting<T>(path: string) {
  const apiFetch = useApi()
  return useQuery({
    queryKey: ['settings', path],
    queryFn: async () => {
      const res = await apiFetch<T>(`/api/settings/${path}`)
      return res.success ? (res.data ?? null) : null
    },
    staleTime: 5 * 60_000,
  })
}

function download(bytes: Uint8Array<ArrayBuffer>, filename: string) {
  const url = URL.createObjectURL(new Blob([bytes], { type: 'application/zip' }))
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

export function StarterKit() {
  const { data: campaigns, isLoading: loadingCampaigns } = useCampaigns()
  const [campaignId, setCampaignId] = useState('')
  const [copied, setCopied] = useState(false)

  // Default to a campaign Claude writes, else an active one, else the first.
  useEffect(() => {
    if (campaignId || !campaigns?.length) return
    setCampaignId(
      (campaigns.find((c) => readMessageWriter(c) === 'mcp') ?? campaigns.find((c) => c.status === 'active') ?? campaigns[0]!).id,
    )
  }, [campaigns, campaignId])

  const brief = useCampaignBrief(campaignId, Boolean(campaignId))
  const offer = useSetting<OfferConfig>('offer')
  const voice = useSetting<SenderVoiceConfig>('voice')
  const faq = useSetting<FaqConfig>('faq')
  const objections = useSetting<ObjectionConfig>('objections')

  const campaign = campaigns?.find((c) => c.id === campaignId)
  const skill = useMemo(
    () =>
      campaign
        ? buildMessagingSkill({
            campaignName: campaign.name,
            brief: normalizeCampaignBrief(brief.data?.brief),
            offer: offer.data,
            voice: voice.data,
            faq: faq.data,
            objections: objections.data,
          })
        : null,
    [campaign, brief.data, offer.data, voice.data, faq.data, objections.data],
  )
  const building = brief.isLoading || offer.isLoading || voice.isLoading || faq.isLoading || objections.isLoading

  return (
    <Card id="claude-starter-kit" className="scroll-mt-4">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Sparkles className="h-4 w-4" />
          Starter kit: teach Claude your offer
        </CardTitle>
        <CardDescription>
          A skill tells Claude what you sell, who it is for, your proof, FAQs and tone. We build it from your
          campaign brief and Settings, so you don&apos;t type anything twice.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loadingCampaigns ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading campaigns...
          </p>
        ) : !campaigns?.length ? (
          <p className="rounded-lg border border-dashed p-4 text-center text-sm text-muted-foreground">
            Create a campaign first. Its messaging brief is what the skill is built from.{' '}
            <Link to="/campaigns" className="underline">Go to campaigns</Link>
          </p>
        ) : (
          <>
            <div className="space-y-1.5">
              <p className="text-sm font-medium">Build it from this campaign</p>
              <Select value={campaignId} onValueChange={setCampaignId}>
                <SelectTrigger className="w-full sm:w-80">
                  <SelectValue placeholder="Pick a campaign" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {building || !skill ? (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Building your skill...
              </p>
            ) : (
              <>
                {skill.missing.length > 0 && (
                  <p className="rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs">
                    Still empty: {skill.missing.join(', ')}. Add them in the campaign&apos;s messaging brief or in{' '}
                    <Link to="/settings?tab=outreach" className="underline">Settings, Offer</Link>, and Claude will
                    have less to guess. The skill works without them.
                  </p>
                )}
                <pre
                  className="max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted px-3 py-2 text-xs"
                  data-testid="starter-kit-skill"
                >
                  {skill.markdown}
                </pre>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" size="sm" onClick={() => download(skillZip(skill.markdown), `${SKILL_NAME}.zip`)}>
                    <Download className="mr-1 h-3.5 w-3.5" />
                    Download skill (.zip)
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      void navigator.clipboard.writeText(skill.markdown).then(() => {
                        setCopied(true)
                        setTimeout(() => setCopied(false), 1500)
                      })
                    }}
                  >
                    {copied ? <Check className="mr-1 h-3.5 w-3.5" /> : <Copy className="mr-1 h-3.5 w-3.5" />}
                    {copied ? 'Copied' : 'Copy text'}
                  </Button>
                </div>
              </>
            )}

            <ol className="list-decimal space-y-1 pl-5 text-sm text-muted-foreground">
              <li>
                <span className="text-foreground">claude.ai or Claude Desktop:</span> Settings, then Capabilities,
                then upload the zip under Skills. No Skills there? Paste the copied text into a Project&apos;s
                instructions and run your DealCloser chats in that Project.
              </li>
              <li>
                <span className="text-foreground">Claude Code:</span> save the text as{' '}
                <code className="rounded bg-muted px-1 text-xs">~/.claude/skills/{SKILL_NAME}/SKILL.md</code>.
              </li>
              <li>
                Test it: say &quot;Do my DealCloser messaging in draft only mode&quot; and read the drafts in the
                review queue at the top of <Link to="/inbox" className="underline">Inbox</Link>.
              </li>
            </ol>
            <p className="text-xs text-muted-foreground">
              Changed your brief or offer? Download the skill again and replace the old one. Claude also reads each
              campaign&apos;s latest brief on every message, so a stale skill never overrides it.
            </p>
          </>
        )}
      </CardContent>
    </Card>
  )
}

function CopyLine({ text }: { readonly text: string }) {
  const [copied, setCopied] = useState(false)
  return (
    <li className="flex items-start justify-between gap-2 rounded-md border px-3 py-2 text-sm">
      <span>&quot;{text}&quot;</span>
      <button
        type="button"
        className="shrink-0 text-muted-foreground hover:text-foreground"
        aria-label={copied ? 'Copied' : 'Copy this prompt'}
        onClick={() => {
          void navigator.clipboard.writeText(text).then(() => {
            setCopied(true)
            setTimeout(() => setCopied(false), 1500)
          })
        }}
      >
        {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
      </button>
    </li>
  )
}

/** "What you can ask Claude": everyday reads, so users see what the connection is for. */
export function AskClaude() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquareText className="h-4 w-4" />
          What you can ask Claude
        </CardTitle>
        <CardDescription>
          Once DealCloser is connected, ask in plain words in any Claude chat. Claude only sees your own account. Copy
          one to try it.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {ASK_CLAUDE_EXAMPLES.map((g) => (
          <div key={g.group} className="space-y-1.5">
            <p className="text-sm font-medium">{g.group}</p>
            <ul className="space-y-1.5">
              {g.prompts.map((p) => (
                <CopyLine key={p} text={p} />
              ))}
            </ul>
          </div>
        ))}
        <p className="text-xs text-muted-foreground">
          Research needs Pro and a connection that can write. Writing and sending messages for you is below in{' '}
          <span className="font-medium">Let Claude run your messaging</span>.
        </p>
      </CardContent>
    </Card>
  )
}

/** What works today, in short. The long version is docs/mcp/starter-kit/limits.md. */
export function StarterKitLimits() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Good to know</CardTitle>
      </CardHeader>
      <CardContent>
        <ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
          <li>Scheduled tasks need a paid Claude plan. On the free plan, paste the prompt into a chat by hand.</li>
          <li>
            Claude sometimes starts a scheduled run without its connector tools. Read the first few runs. If one says
            the DealCloser tools did not load, open it and send &quot;continue&quot;.
          </li>
          <li>
            Pro and Custom can let Claude write and send. Basic connects read only: Claude can read your campaigns,
            replies and leads, and check a message, but not save one.
          </li>
          <li>
            Every message still goes through DealCloser&apos;s quality check: below 6 out of 10 is rejected, 6 to 7.9
            waits for your review, 8 or higher can send.
          </li>
          <li>Limits: 120 tool calls a minute and 3,000 a day per account, up to 5 active connections.</li>
          <li>ChatGPT is not supported yet. Its write connectors need a sign-in method DealCloser doesn&apos;t offer.</li>
        </ul>
      </CardContent>
    </Card>
  )
}
